<template>
  <div class="report-page">
    <NavBar />
    <div class="report-header">
      <div class="container">
        <h1 class="page-title">
          {{ $t('report.title') }}
        </h1>
      </div>
    </div>

    <div class="report-content">
      <div class="container">
        <van-form
          ref="formRef"
          class="report-form"
          @submit="handleSubmit"
        >
          <!-- 品牌信息区域 -->
          <div class="form-section">
            <h3 class="section-title">
              {{ $t('report.brandInfo') }}
            </h3>

            <!-- 品牌选择/输入 -->
            <div class="brand-field">
              <van-field
                v-model="reportForm.brandName"
                :label="$t('report.brand')"
                :placeholder="$t('report.brandPlaceholder')"
                :rules="[{ required: true, message: $t('report.brandRequired') }]"
                readonly
                clickable
                @click="showBrandPicker = true"
              />
              <van-icon
                name="arrow"
                class="field-arrow"
              />
            </div>

            <!-- 品牌选择器 -->
            <van-popup
              v-model:show="showBrandPicker"
              position="bottom"
            >
              <van-picker
                :columns="brandOptions"
                show-toolbar
                :title="$t('report.selectBrand')"
                @confirm="onBrandConfirm"
                @cancel="showBrandPicker = false"
              />
            </van-popup>

            <!-- 自定义品牌输入 -->
            <!--            <van-field-->
            <!--              v-if="reportForm.brandName === 'other'"-->
            <!--              v-model="reportForm.customBrand"-->
            <!--              :label="$t('report.customBrand')"-->
            <!--              :placeholder="$t('report.customBrandPlaceholder')"-->
            <!--              :rules="[{ required: true, message: $t('report.customBrandRequired') }]"-->
            <!--            />-->

            <!-- 产品名称 -->
            <van-field
              v-model="reportForm.productName"
              :label="$t('report.productName')"
              :placeholder="$t('report.productNamePlaceholder')"
              :rules="[{ required: true, message: $t('report.productNameRequired') }]"
            />
          </div>

          <!-- 购买信息区域 -->
          <div class="form-section">
            <h3 class="section-title">
              {{ $t('report.purchaseInfo') }}
            </h3>

            <!-- 购买渠道 -->
            <van-field
              v-model="reportForm.purchaseChannel"
              :label="$t('report.purchaseChannel')"
              :placeholder="$t('report.purchaseChannelPlaceholder')"
              :rules="[{ required: true, message: $t('report.purchaseChannelRequired') }]"
            />

            <!-- 购买时间 -->
            <van-field
              v-model="reportForm.purchaseDate"
              :label="$t('report.purchaseDate')"
              :placeholder="$t('report.purchaseDatePlaceholder')"
              :rules="[{ required: true, message: $t('report.purchaseDateRequired') }]"
              readonly
              clickable
              @click="showDatePicker = true"
            />

            <!-- 日期选择器 -->
            <van-popup
              v-model:show="showDatePicker"
              position="bottom"
            >
              <van-date-picker
                v-model="currentDate"
                :min-date="minDate"
                :max-date="maxDate"
                :title="$t('report.selectDate')"
                @confirm="onDateConfirm"
                @cancel="showDatePicker = false"
              />
            </van-popup>
          </div>

          <!-- 问题描述区域 -->
          <div class="form-section">
            <h3 class="section-title">
              {{ $t('report.problemInfo') }}
            </h3>

            <!-- 问题描述 -->
            <van-field
              v-model="reportForm.problemDescription"
              rows="4"
              autosize
              type="textarea"
              :label="$t('report.problemDescription')"
              :placeholder="$t('report.problemDescriptionPlaceholder')"
              :rules="[{ required: true, message: $t('report.problemDescriptionRequired') }]"
              maxlength="500"
              show-word-limit
            />

            <!-- 证据照片 -->
            <!--            <div class="upload-section">-->
            <!--              <div class="upload-label">-->
            <!--                {{ $t('report.evidencePhotos') }}-->
            <!--              </div>-->
            <!--              <van-uploader-->
            <!--                v-model="fileList"-->
            <!--                multiple-->
            <!--                :max-count="5"-->
            <!--                :after-read="afterRead"-->
            <!--                :preview-size="80"-->
            <!--                @delete="onDelete"-->
            <!--              >-->
            <!--                <template #preview-cover="{ file }">-->
            <!--                  <div class="preview-cover">-->
            <!--                    <van-icon name="photo" />-->
            <!--                    {{file}}-->
            <!--                  </div>-->
            <!--                </template>-->
            <!--              </van-uploader>-->
            <!--              <p class="upload-tip">-->
            <!--                {{ $t('report.uploadTip') }}-->
            <!--              </p>-->
            <!--            </div>-->
          </div>

          <!-- 联系信息区域 -->
          <div class="form-section">
            <h3 class="section-title">
              {{ $t('report.contactInfo') }}
            </h3>

            <!-- 匿名举报开关 -->
            <div class="anonymous-section">
              <div class="anonymous-label">
                <span>{{ $t('report.anonymousReport') }}</span>
                <van-switch
                  v-model="reportForm.isAnonymous"
                  size="20"
                  @change="onAnonymousChange"
                />
              </div>
              <p class="anonymous-tip">
                {{ $t('report.anonymousTip') }}
              </p>
            </div>

            <!-- 联系信息（非匿名时显示） -->
            <div
              v-if="!reportForm.isAnonymous"
              class="contact-fields"
            >
              <van-field
                v-model="reportForm.contactName"
                :label="$t('report.contactName')"
                :placeholder="$t('report.contactNamePlaceholder')"
                :rules="[{ required: !reportForm.isAnonymous, message: $t('report.contactNameRequired') }]"
              />

              <van-field
                v-model="reportForm.contactPhone"
                :label="$t('report.contactPhone')"
                :placeholder="$t('report.contactPhonePlaceholder')"
                :rules="[
                  { required: !reportForm.isAnonymous, message: $t('report.contactPhoneRequired') },
                  { validator: validatePhone, message: $t('report.phoneFormatError') }
                ]"
                type="tel"
              />
            </div>
          </div>

          <!-- 提交按钮 -->
          <div class="form-actions">
            <van-button
              round
              block
              type="primary"
              native-type="submit"
              :loading="submitting"
              class="submit-button"
            >
              {{ submitting ? $t('report.submitting') : $t('report.submitReport') }}
            </van-button>

            <!--            <p class="agreement-tip">-->
            <!--              {{ $t('report.agreementTip') }}-->
            <!--              <a-->
            <!--                href="/privacy"-->
            <!--                class="agreement-link"-->
            <!--              >{{ $t('report.userAgreement') }}</a>-->
            <!--            </p>-->
          </div>
        </van-form>
      </div>
    </div>

    <!-- 提交成功弹窗 -->
    <!--    <van-dialog-->
    <!--      v-model:show="showSuccessDialog"-->
    <!--      :title="$t('report.submitSuccess')"-->
    <!--      :show-confirm-button="false"-->
    <!--    >-->
    <!--      <div class="success-dialog">-->
    <!--        <van-icon-->
    <!--          name="checked"-->
    <!--          class="success-icon"-->
    <!--        />-->
    <!--        <p>{{ $t('report.successMessage') }}</p>-->
    <!--        <p>{{ $t('report.successMessage') }}</p>-->
    <!--        <div class="dialog-actions">-->
    <!--          <van-button-->
    <!--            type="primary"-->
    <!--            class="dialog-button"-->
    <!--            @click="handleViewReport"-->
    <!--          >-->
    <!--            {{ $t('report.viewReport') }}-->
    <!--          </van-button>-->
    <!--        </div>-->
    <!--      </div>-->
    <!--    </van-dialog>-->
    <!-- 提交成功弹窗 -->
    <van-dialog
      v-model:show="showSuccessDialog"
      :title="$t('report.submitSuccess')"
      :show-confirm-button="false"
      class="success-dialog-container"
    >
      <div class="success-dialog">
        <div class="success-header">
          <van-icon
            name="checked"
            class="success-icon"
          />
          <h3>{{ $t('report.thankYou') }}</h3>
        </div>

        <div class="success-content">
          <p class="success-message">
            {{ $t('report.successMessage') }}
          </p>
          <p
            v-if="reportCode"
            class="case-number"
          >
            {{ $t('report.caseNumber') }}: <strong>{{ reportCode }}</strong>
          </p>
        </div>

        <!-- 进度查询二维码区域 -->
        <div class="qr-section">
          <h4 class="qr-title">
            {{ $t('report.trackProgress') }}
          </h4>
          <p class="qr-description">
            {{ $t('report.scanToTrack') }}
          </p>

          <div class="qr-code-container">
            <img
              :src="reportQrCode"
              :alt="$t('report.progressQrAlt')"
              class="qr-code"
            >
            <!--            <div-->
            <!--              v-if="!progressQrCode"-->
            <!--              class="qr-overlay"-->
            <!--            >-->
            <!--              <van-loading type="spinner" />-->
            <!--              <p>{{ $t('report.generatingQr') }}</p>-->
            <!--            </div>-->
          </div>

          <!--          <div class="qr-tips">-->
          <!--            <van-icon-->
          <!--              name="info"-->
          <!--              class="tip-icon"-->
          <!--            />-->
          <!--            <span>{{ $t('report.qrTip') }}</span>-->
          <!--          </div>-->
        </div>

        <div class="dialog-actions">
          <van-button
            type="primary"
            class="dialog-button primary-button"
            @click="handleViewReport"
          >
            <van-icon name="eye" />
            {{ $t('report.viewReport') }}
          </van-button>
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { showToast, showSuccessToast, showFailToast } from 'vant'
import type { UploaderFileListItem } from 'vant'
import NavBar from '@/components/NavBar.vue'
import { ReportParams, submitReport, uploadEvidence } from '@/api/report'
import { queryBrandList } from '@/api/brand'
import type { Brand } from '@/api/brand'
import { uploadFileByPublic, UploadRequest } from '@/api/common'

export default {
  name: 'Report',
  components: {
    NavBar
  },
  setup () {
    const router = useRouter()
    const formRef = ref()
    // 表单数据
    const reportForm = reactive({
      brandId: 0,
      brandName: '',
      productName: '',
      purchaseChannel: '',
      purchaseDate: '',
      problemDescription: '',
      evidencePhotos: [],
      isAnonymous: true,
      contactName: '',
      contactPhone: ''
    })

    // UI状态
    const showBrandPicker = ref(false)
    const showDatePicker = ref(false)
    const showSuccessDialog = ref(false)
    const submitting = ref(false)

    // 品牌选项
    const brandOptions = ref<{ text: string; value: number }[]>([])
    const fileList = ref<UploaderFileListItem[]>([])
    const uploadedFiles = ref<string[]>([])

    // 日期相关
    const currentDate = ref(['2023', '01', '01'])
    const minDate = new Date(2010, 0, 1)
    const maxDate = new Date()

    // 初始化品牌选项
    const initBrandOptions = async () => {
      const param = {
        page: 1,
        size: 99
      }
      queryBrandList(param).then(res => {
        brandOptions.value = res.list.map((brand: Brand) => ({
          text: brand.name,
          value: brand.id
        }))
        // brandOptions.value.push({
        //   text: '其他品牌',
        //   value: 'other'
        // })
      })
    }

    // 品牌选择确认
    const onBrandConfirm = (value: { selectedValues: number[], selectedOptions: any[] }) => {
      reportForm.brandId = value.selectedValues[0]
      reportForm.brandName = value.selectedOptions[0].text
      showBrandPicker.value = false
    }

    // 日期选择确认
    const onDateConfirm = (value: any) => {
      const date = new Date(value.selectedValues.join('-'))
      reportForm.purchaseDate = date.toISOString().split('T')[0]
      showDatePicker.value = false
    }

    // 匿名状态改变
    const onAnonymousChange = (value: boolean) => {
      reportForm.isAnonymous = value
      if (value) {
        // 匿名时清空联系信息
        reportForm.contactName = ''
        reportForm.contactPhone = ''
      }
    }

    // 文件上传处理
    const afterRead = async (file: UploaderFileListItem) => {
      file.status = 'uploading'
      file.message = '上传中...'
      const formData = new FormData()
      if (file.file) {
        formData.append('file', file.file)
      }
      formData.append('fileName', '证据')
      uploadFileByPublic(formData).then(res => {
        //
        // console.log('uploadFileByPublic', res)
      }).catch(error => {
        console.log('uploadFileByPublic error', error)
      })
    }

    // 文件删除处理
    const onDelete = (file: UploaderFileListItem) => {
      const index = uploadedFiles.value.findIndex(url => url === file.url)
      if (index > -1) {
        uploadedFiles.value.splice(index, 1)
      }
    }

    // 手机号验证
    const validatePhone = (value: string) => {
      const phoneRegex = /^1[3-9]\d{9}$/
      return phoneRegex.test(value)
    }

    const reportQrCode = ref('https://cdn-oss-dev.buttonpie.cn/admin-service/team/6b6f8344-1db2-48a6-babe-481622732f81.png?x-oss-process=style/mark')
    const reportCode = ref('CP202312150001')
    // 表单提交
    const handleSubmit = async () => {
      if (submitting.value) return

      submitting.value = true

      try {
        // 准备提交数据
        const isAnonymous = reportForm.isAnonymous === true ? 1 : 0
        const submitData:ReportParams = {
          brandId: reportForm.brandId,
          brandName: reportForm.brandName,
          productName: reportForm.productName,
          purchaseDate: reportForm.purchaseDate + ' 00:00:00',
          purchaseChannel: reportForm.purchaseChannel,
          problemDescription: reportForm.problemDescription,
          // evidenceImages: uploadedFiles.value,
          isAnonymous: isAnonymous,
          contactName: reportForm.contactName,
          contactPhone: reportForm.contactPhone
        }

        // 提交举报
        const result = await submitReport(submitData)
        reportCode.value = result.code
        // 显示成功提示
        showSuccessDialog.value = true
      } catch (error: any) {
        console.error('提交举报失败:', error)
        // showFailToast(error.message || '提交失败，请稍后重试')
      } finally {
        submitting.value = false
      }
    }

    const handleViewReport = () => {
      showSuccessDialog.value = false
      router.push({
        path: '/report/progress',
        query: {
          code: reportCode.value
        }
      })
    }

    onMounted(() => {
      initBrandOptions()
      // 设置默认日期为今天
      const today = new Date()
      reportForm.purchaseDate = today.toISOString().split('T')[0]
      currentDate.value = [
        today.getFullYear().toString(),
        (today.getMonth() + 1).toString().padStart(2, '0'),
        today.getDate().toString().padStart(2, '0')
      ]
    })
    return {
      formRef,
      reportForm,
      showBrandPicker,
      showDatePicker,
      showSuccessDialog,
      submitting,
      onDateConfirm,
      onBrandConfirm,
      onAnonymousChange,
      uploadedFiles,
      afterRead,
      onDelete,
      validatePhone,
      handleSubmit,
      brandOptions,
      minDate,
      maxDate,
      currentDate,
      // showFailToast,
      // showSuccessToast,
      fileList,
      reportQrCode,
      reportCode,
      handleViewReport
    }
  }
}

// 查看举报记录
// const handleViewReport = () => {
//   showSuccessDialog.value = false
//   router.push('/reports')
// }

// 继续提交举报
// const handleSubmitAnother = () => {
//   showSuccessDialog.value = false
//   // 重置表单
//   Object.assign(formData, {
//     brandName: '',
//     customBrand: '',
//     productName: '',
//     purchaseChannel: '',
//     purchaseDate: '',
//     problemDescription: '',
//     isAnonymous: true,
//     contactName: '',
//     contactPhone: ''
//   })
//   fileList.value = []
//   uploadedFiles.value = []
//
//   // 重置表单验证
//   setTimeout(() => {
//     reportForm.value?.resetValidation()
//   }, 100)
// }
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.report-page {
  min-height: 100vh;
  background-color: $light-gray;
}

.report-header {
  background: linear-gradient(135deg, $primary-blue 0%, darken($primary-blue, 10%) 100%);
  color: $white;
  padding: $spacing-xl 0;
  text-align: center;
}

.page-title {
  font-size: $font-size-xxl;
  font-weight: 700;
  margin-bottom: $spacing-sm;
}

.page-subtitle {
  font-size: $font-size-lg;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
}

.report-content {
  padding: $spacing-xl 0;
}

.report-form {
  background: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-xl;
  box-shadow: $card-shadow;
}

.form-section {
  margin-bottom: $spacing-xl;
  padding-bottom: $spacing-lg;
  border-bottom: 1px solid rgba($light-gray, 0.5);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.section-title {
  font-size: $font-size-lg;
  font-weight: 600;
  margin-bottom: $spacing-lg;
  color: $primary-blue;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 16px;
    background: $accent-gold;
    margin-right: $spacing-sm;
    border-radius: 2px;
  }
}

.brand-field {
  position: relative;

  .field-arrow {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-light;
    pointer-events: none;
  }
}

.upload-section {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  .upload-label {
    font-size: $font-size-md;
    color: $primary-blue;
    margin-bottom: $spacing-sm;
    font-weight: 400;
    font-size: 14px;
  }

  .upload-tip {
    font-size: $font-size-xs;
    color: $text-light;
    margin-top: $spacing-sm;
  }

  .preview-cover {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    color: $white;
    font-size: 18px;
  }
}

.anonymous-section {
  .anonymous-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $spacing-sm;
    font-weight: 500;
  }

  .anonymous-tip {
    font-size: $font-size-sm;
    color: $text-light;
    line-height: 1.4;
  }
}

.contact-fields {
  margin-top: $spacing-lg;
  padding-top: $spacing-lg;
  border-top: 1px dashed rgba($light-gray, 0.5);
}

.form-actions {
  margin-top: $spacing-xl;
  text-align: center;

  .submit-button {
    margin-bottom: $spacing-lg;
    height: 50px;
    font-size: $font-size-md;
    font-weight: 600;
  }

  .agreement-tip {
    font-size: $font-size-xs;
    color: $text-light;
    line-height: 1.4;

    .agreement-link {
      color: $accent-gold;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.success-dialog-container {
  .van-dialog {
    max-width: 90%;
    width: 400px;
    border-radius: 16px;
    overflow: hidden;
  }

  .van-dialog__header {
    padding: 20px 20px 10px;
    font-weight: 600;
    font-size: 18px;
    color: #333;
  }

  .van-dialog__content {
    padding: 0 20px 20px;
  }
}

.success-dialog {
  text-align: center;

  .success-header {
    margin-bottom: 16px;

    .success-icon {
      font-size: 48px;
      color: #07c160;
      margin-bottom: 8px;
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #333;
    }
  }

  .success-content {
    margin-bottom: 20px;

    .success-message {
      margin: 0 0 12px;
      font-size: 14px;
      color: #666;
      line-height: 1.5;
    }

    .case-number {
      margin: 0;
      font-size: 14px;
      color: #333;

      strong {
        color: #1989fa;
        font-weight: 600;
      }
    }
  }

  .qr-section {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;

    .qr-title {
      margin: 0 0 8px;
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }

    .qr-description {
      margin: 0 0 16px;
      font-size: 14px;
      color: #666;
    }

    .qr-code-container {
      position: relative;
      display: inline-block;
      margin-bottom: 12px;

      .qr-code {
        width: 140px;
        height: 140px;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        background: white;
        padding: 8px;
      }

      .qr-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 8px;

        p {
          margin: 8px 0 0;
          font-size: 12px;
          color: #999;
        }
      }
    }

    .qr-tips {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: #999;

      .tip-icon {
        margin-right: 4px;
        font-size: 14px;
      }
    }
  }

  .dialog-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .dialog-button {
      height: 44px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;

      .van-icon {
        margin-right: 6px;
      }

      &.primary-button {
        background: linear-gradient(135deg, #1989fa 0%, #007aff 100%);
        border: none;
        color: white;
      }

      &.secondary-button {
        border-color: #dcdfe6;
        color: #606266;

        &:hover {
          border-color: #1989fa;
          color: #1989fa;
        }
      }
    }
  }
}

/* 响应式适配 */
@media (max-width: 480px) {
  .success-dialog-container {
    .van-dialog {
      width: 95%;
    }

    .van-dialog__content {
      padding: 0 16px 16px;
    }
  }

  .success-dialog {
    .qr-section {
      padding: 12px;

      .qr-code-container .qr-code {
        width: 120px;
        height: 120px;
      }
    }

    .dialog-actions .dialog-button {
      height: 40px;
      font-size: 13px;
    }
  }
}

// 响应式设计
@media (max-width: $breakpoint-md) {
  .report-header {
    padding: $spacing-lg 0;
  }

  .page-title {
    font-size: $font-size-xl;
  }

  .page-subtitle {
    font-size: $font-size-md;
  }

  .report-content {
    padding: $spacing-lg 0;
  }

  .report-form {
    padding: $spacing-lg;
  }

  .form-section {
    margin-bottom: $spacing-lg;
    padding-bottom: $spacing-md;
  }

  .dialog-actions {
    flex-direction: column;
  }
}

@media (max-width: $breakpoint-sm) {
  .page-title {
    font-size: $font-size-lg;
  }

  .page-subtitle {
    font-size: $font-size-sm;
  }

  .report-form {
    padding: $spacing-md;
  }

  .section-title {
    font-size: $font-size-md;
  }
}
</style>
